/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { DetailsHeader, IColumn, IDetailsHeaderProps, IDetailsRowProps } from '@fluentui/react/lib/DetailsList';
import { IScrollablePaneProps, IScrollablePaneStyles, ScrollablePane } from '@fluentui/react/lib/ScrollablePane';
import { Stack } from '@fluentui/react/lib/Stack';
import { Sticky } from '@fluentui/react/lib/Sticky';
import { classNamesFunction, IRenderFunction, SelectionMode } from '@fluentui/react/lib/Utilities';
import { observer } from 'mobx-react';
import React from 'react';
import { Table } from 'src/core/components/Table';
import { TableProps } from 'src/core/components/TableProps';
import { getScopedTheme, getStylistV2 } from 'src/core/themes/luisStylist';
import { QnaMakerSynonymRowRenderer } from 'src/qnaMaker/appShell/qnaMakerEditPage/components/QnaMakerSynonymRowRenderer';
import { QnaMakerSynonymsColumnRenderer } from 'src/qnaMaker/appShell/qnaMakerEditPage/components/QnaMakerSynonymsColumnRenderer';
import { QnaMakerSynonymsUiStore } from 'src/qnaMaker/appShell/qnaMakerEditPage/QnaMakerSynonymsUiStore';
import { QnaMakerSynonymSwagger } from 'src/qnaMaker/core/services/qnas/QnaMakerAssetsSwaggers';
import { QnaMakerSynonymStore } from 'src/qnaMaker/core/stores/QnaMakerSynonymStore';

export type QnaMakerSynonymTableProps = TableProps<QnaMakerSynonymStore> & {
    isAddSynonymOn: boolean;
    synonymsStore: QnaMakerSynonymsUiStore;
    synonymsBeingEditedId: number;
    onAddSynonym: (value: boolean) => void;
    onDismissEditSynonym: (id: number) => void;
    onDismissAddSynonym: () => void;
    onSubmitEditSynonym: (synonymId: number, newData: Partial<QnaMakerSynonymSwagger>) => void;
    onSubmitAddSynonym: (newData: Partial<QnaMakerSynonymSwagger>) => void;
};

const { getClassName } = getStylistV2('QnaMakerSynonymTable');

const tableCheckboxClassName = getClassName('tableCheckboxClassName', {
    top: 2
});

const ScrollablePaneStyles = classNamesFunction<IScrollablePaneProps, IScrollablePaneStyles>()({
    root: { minHeight: '200px', height: '100%', position: 'relative' }
});

const tableTheme = getScopedTheme('Table');

/**
 * A component displaying question and answer pairs along with their details.
 */
export const QnaMakerSynonymTable = observer((props: QnaMakerSynonymTableProps) => {
    const {
        isAddSynonymOn,
        synonymsBeingEditedId,
        onAddSynonym,
        onDismissEditSynonym,
        onSubmitEditSynonym,
        onSubmitAddSynonym,
        onDismissAddSynonym,
        synonymsStore
    } = props;

    const onRenderSynonyms = (item: QnaMakerSynonymStore) => (
        <QnaMakerSynonymsColumnRenderer
            isInEditMode={item.id === synonymsBeingEditedId}
            synonyms={item.alterations}
            onEditSynonyms={synonyms => onSubmitEditSynonym(item.id, { id: item.id, alterations: synonyms })}
        />
    );

    const onRenderHeader: IRenderFunction<IDetailsHeaderProps> = headerProps => (
        <Stack>
            <Sticky>
                <DetailsHeader {...headerProps} />
            </Sticky>

            {isAddSynonymOn && (
                <QnaMakerSynonymRowRenderer
                    rowProps={{ ...headerProps, item: null, itemIndex: 0, styles: undefined }}
                    synonymsStore={synonymsStore}
                    synonymsBeingEditedId={synonymsBeingEditedId}
                    onAddSynonym={onAddSynonym}
                    onSubmitChanges={newData => onSubmitAddSynonym(newData)}
                    onDismissChanges={onDismissAddSynonym}
                />
            )}
        </Stack>
    );

    const onRenderRow: IRenderFunction<IDetailsRowProps> = rowProps => (
        <QnaMakerSynonymRowRenderer
            rowProps={rowProps}
            synonymsStore={synonymsStore}
            synonymsBeingEditedId={synonymsBeingEditedId}
            onAddSynonym={onAddSynonym}
            onSubmitChanges={newData => onSubmitEditSynonym(newData.id, { ...newData })}
            onDismissChanges={onDismissEditSynonym}
        />
    );

    const columns: IColumn[] = [
        {
            key: 'synonyms',
            minWidth: 1200,
            maxWidth: 1500,
            isRowHeader: false,
            fieldName: 'synonyms',
            isResizable: true,
            isCollapsible: false,
            name: 'Synonyms',
            onRender: onRenderSynonyms,
            styles: { cellName: { color: tableTheme.headerCellNameColor, fontWeight: tableTheme.headerCellNameFontWeight } }
        }
    ];

    return (
        <ScrollablePane styles={ScrollablePaneStyles}>
            <Table
                columns={columns}
                onRenderRow={onRenderRow}
                onRenderDetailsHeader={onRenderHeader}
                selectionMode={SelectionMode.multiple}
                checkboxCellClassName={tableCheckboxClassName}
                {...props}
            />
        </ScrollablePane>
    );
});
